/**
 * Firebase の初期化(唯一の入口)。
 * - APIキーのハードコード禁止。値はすべて .env(VITE_FIREBASE_*)から読む
 * - 本番プロジェクトを作ったら、prod への接続は VITE_APP_ENV=prod のビルドのみに限定する
 */
import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  browserSessionPersistence,
  indexedDBLocalPersistence,
  connectAuthEmulator,
  initializeAuth,
  type Auth,
} from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator, type Firestore } from 'firebase/firestore';
import { getFunctions, connectFunctionsEmulator, type Functions } from 'firebase/functions';
import { isPreviewHost } from './hosts';

/** 本番URLの追加分(ビルド時の環境変数。カンマ区切り)。基本の一覧は hosts.ts の PRODUCTION_HOSTS */
const extraProductionHosts = import.meta.env.VITE_PRODUCTION_HOSTS as string | undefined;

const REQUIRED_KEYS = [
  'VITE_FIREBASE_API_KEY',
  'VITE_FIREBASE_AUTH_DOMAIN',
  'VITE_FIREBASE_PROJECT_ID',
  'VITE_FIREBASE_STORAGE_BUCKET',
  'VITE_FIREBASE_MESSAGING_SENDER_ID',
  'VITE_FIREBASE_APP_ID',
] as const;

function readConfig() {
  const missing = REQUIRED_KEYS.filter((k) => !import.meta.env[k]);
  if (missing.length > 0) {
    throw new Error(
      `.env の設定が不足しています: ${missing.join(', ')}\n` +
        '.env.example をコピーして .env を作り、Firebaseコンソールの値を入れてください。',
    );
  }
  return {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
  };
}

export const appEnv: 'dev' | 'prod' = import.meta.env.VITE_APP_ENV === 'prod' ? 'prod' : 'dev';

/**
 * 使う Firestore データベース(2026-09-12。D-65)。
 * 本番は `(default)`、テストは同じプロジェクト内の `test`。データは完全に別で同期しない。
 * 未設定なら `(default)`(本番)。**テストのつもりで本番を書き換えないよう、prod ビルド以外で
 * `(default)` を指していたら止める**(下の ensureApp)
 */
export const firestoreDbId: string = (import.meta.env.VITE_FIRESTORE_DB as string | undefined)?.trim() || '(default)';

/** 副次アプリ(管理者によるユーザー作成)の初期化に使う。通常の画面からは呼ばない */
export function getFirebaseConfig() {
  return readConfig();
}

let _app: FirebaseApp | null = null;
let _auth: Auth | null = null;
let _db: Firestore | null = null;

function ensureApp(): FirebaseApp {
  if (_app) return _app;
  const config = readConfig();

  // 事故防止: dev ビルドで本番プロジェクトに繋がっていたら止める
  if (appEnv === 'dev' && config.projectId.includes('prod')) {
    throw new Error(
      `開発ビルドが本番プロジェクト(${config.projectId})に接続しようとしています。.env を確認してください。`,
    );
  }
  // 事故防止2: 本番ビルド以外がエミュレータでも `test` でもなく、本番の (default) を指している
  if (appEnv !== 'prod' && firestoreDbId === '(default)' && import.meta.env.VITE_USE_EMULATOR !== 'true') {
    throw new Error(
      '開発ビルドが本番のデータベース((default))に接続しようとしています。VITE_FIRESTORE_DB=test を設定するか、エミュレータを使ってください。',
    );
  }

  // 事故防止3: 本番以外の *.pages.dev(develop のプレビュー・テスト環境)が本番の (default) を指していたら止める。
  // 本番URLは新旧どちらも hosts.ts で判定する
  if (
    typeof window !== 'undefined' &&
    isPreviewHost(window.location.hostname, extraProductionHosts) &&
    firestoreDbId === '(default)'
  ) {
    throw new Error(
      'プレビュー環境が本番のデータベース((default))に接続しようとしています。プレビューのビルドには VITE_FIRESTORE_DB=test を設定してください。',
    );
  }

  _app = initializeApp(config);

  if (import.meta.env.VITE_USE_EMULATOR === 'true') {
    connectAuthEmulator(getFirebaseAuth(), 'http://127.0.0.1:9099', { disableWarnings: true });
    connectFirestoreEmulator(getFirestore(_app, firestoreDbId), '127.0.0.1', 8080);
  }
  return _app;
}

export function getFirebaseApp(): FirebaseApp {
  return ensureApp();
}

export function getFirebaseAuth(): Auth {
  if (!_auth) {
    // テスト環境のプレビュー配信(*.pages.dev のブランチURL)や ?tab(値なし)・?tab=1 で開いたタブは
    // ログインをタブ単位に保ち、同じブラウザの別タブに別ロールで入れる(検証用)。
    // シフト・Q&A の ?tab=month などは画面のタブなので対象外(2026-09-22 修正: 以前はシフトの画面で再読み込みするとログアウトした)。
    // それ以外はログインしたまま(IndexedDB)にする — yojitsu-system と同じ方式
    const tabScoped = (() => {
      try {
        const host = window.location.hostname;
        const previewHost = isPreviewHost(host, extraProductionHosts);
        const tabParam = new URLSearchParams(window.location.search).get('tab');
        if (previewHost || tabParam === '' || tabParam === '1') {
          sessionStorage.setItem('auth-scope', 'tab');
        }
        return sessionStorage.getItem('auth-scope') === 'tab';
      } catch {
        return false;
      }
    })();
    _auth = initializeAuth(ensureApp(), {
      persistence: tabScoped ? browserSessionPersistence : indexedDBLocalPersistence,
    });
  }
  return _auth;
}

export function getDb(): Firestore {
  if (!_db) _db = getFirestore(ensureApp(), firestoreDbId);
  return _db;
}

let _functions: Functions | null = null;

/** Cloud Functions(東京リージョン)。BlueBean API の取得など、サーバー側だけで行う処理の呼び出しに使う */
export function getFirebaseFunctions(): Functions {
  if (!_functions) {
    _functions = getFunctions(ensureApp(), 'asia-northeast1');
    if (import.meta.env.VITE_USE_EMULATOR === 'true') connectFunctionsEmulator(_functions, '127.0.0.1', 5001);
  }
  return _functions;
}

export function getProjectId(): string {
  return readConfig().projectId;
}
